// src/api.js
import axios from 'axios';
import { resolve as resolveAuthHeader } from './authHeader';   // #1898
import moment from 'moment';
import store from './store/store';
import { global } from './global.js';

let isRefreshing = false;
let refreshQueue = [];
let refreshTimer = null;

console.log("api/js loaded");

const LOCK_KEY = 'auth_refresh_lock';
const TOKEN_KEY = 'auth_token';
const EXPIRY_KEY = 'auth_token_expiry';
const REFRESH_AT_KEY = 'auth_refresh_at'; // NEW: when the next refresh should run (ms since epoch)
const BUFFER_MS = 3 * 60 * 1000;         // 3 minutes buffer before expiry
const MIN_DELAY_MS = 60 * 1000;          // never schedule below 60s

// Unique per-tab id (session-scoped)
// #1767 — wipe auth state on logout WITHOUT destroying unsaved-work drafts.
// The workspace writes its serialised workbook under `cs3-draft:` BEFORE it
// POSTs, precisely so a 401 (which redirects and reloads within ~100ms) cannot
// take the work with it. A blanket localStorage.clear() defeated that.
const DRAFT_KEY_PREFIX = 'cs3-draft:';
function clearLocalStorageExceptDrafts() {
  try {
    const keep = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.indexOf(DRAFT_KEY_PREFIX) === 0) {
        keep.push([k, localStorage.getItem(k)]);
      }
    }
    localStorage.clear();
    keep.forEach(([k, v]) => { try { localStorage.setItem(k, v); } catch (_) {} });
  } catch (_) {
    try { localStorage.clear(); } catch (__) {}
  }
}

function getTabId() {
  let id = null;
  try {
    id = sessionStorage.getItem('tab_id');
    if (!id) {
      id = Math.random().toString(36).slice(2);
      sessionStorage.setItem('tab_id', id);
    }
  } catch (_) {
    // sessionStorage might be blocked; fallback
    id = 'tab_' + Math.random().toString(36).slice(2);
  }
  return id;
}
const TAB_ID = getTabId();

// Acquire a refresh "mutex" across tabs (TTL avoids deadlocks)
function acquireRefreshLock(ttlMs) {
  const ttl = ttlMs || 60000; // 60s
  const now = Date.now();
  let raw = null;
  try { raw = localStorage.getItem(LOCK_KEY); } catch (_) { }

  if (raw) {
    try {
      const lock = JSON.parse(raw);
      if (lock && lock.expires && lock.owner && lock.expires > now) return false;
    } catch (_) { }
  }
  const newLock = { owner: TAB_ID, expires: now + ttl };
  try { localStorage.setItem(LOCK_KEY, JSON.stringify(newLock)); } catch (_) { }
  try {
    const confirmRaw = localStorage.getItem(LOCK_KEY);
    const confirm = confirmRaw ? JSON.parse(confirmRaw) : null;
    return !!(confirm && confirm.owner === TAB_ID && confirm.expires > now);
  } catch (_) {
    return false;
  }
}

function releaseRefreshLock() {
  let raw = null;
  try { raw = localStorage.getItem(LOCK_KEY); } catch (_) { }
  if (!raw) return;
  try {
    const lock = JSON.parse(raw);
    if (lock && lock.owner === TAB_ID) localStorage.removeItem(LOCK_KEY);
  } catch (_) {
    try { localStorage.removeItem(LOCK_KEY); } catch (__) { }
  }
}

// Publish new token/expiry so other tabs adopt immediately
function publishToken(token, expiry) {
  try {
    if (token != null) localStorage.setItem(TOKEN_KEY, token);
    if (expiry != null) localStorage.setItem(EXPIRY_KEY, expiry);
  } catch (_) { }
}

// Listen for other tabs updating token/expiry
(function wireStorageListener() {
  try {
    window.addEventListener('storage', (e) => {
      if (!e) return;
      if (e.key !== TOKEN_KEY && e.key !== EXPIRY_KEY) return;

      let token = null, expiry = null;
      try { token = localStorage.getItem(TOKEN_KEY); } catch (_) { }
      try { expiry = localStorage.getItem(EXPIRY_KEY); } catch (_) { }

      if (token) store.dispatch('setUserToken', token);
      if (expiry) store.dispatch('setTokenExpiry', expiry);

      // #1556: another tab just rotated the token. Resolve any requests this tab
      // queued while waiting on a refresh — the lock-holder was the OTHER tab, so
      // our own onRefreshed never fires; drain them with the adopted token so they
      // retry instead of hanging.
      if (token) { try { onRefreshed(token); } catch (_) { } }

      // Re-schedule our proactive timer to the new expiry
      startRefreshTimer();
      console.log('[AUTH] Adopted token/expiry from another tab and rescheduled timer');
    });
  } catch (_) { }
})();


function subscribeTokenRefresh(cb) {
  refreshQueue.push(cb);
  console.log('Queued request for refresh. Queue length: ' + refreshQueue.length);
}

function onRefreshed(newToken) {
  console.log('Processing ' + refreshQueue.length + ' queued requests with new token');
  for (let i = 0; i < refreshQueue.length; i++) {
    try { refreshQueue[i](newToken); } catch (e) { }
  }
  refreshQueue = [];
}

// ---------- timers / time helpers ----------
function clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function getTokenExpiryTime() {
  try {
    const tokenExpiry = store && store.getters ? store.getters.getTokenExpiry : null;
    if (!tokenExpiry) return null;

    // strict parse UTC "YYYY-MM-DD HH:mm:ss"
    let expiryMoment = moment.utc(tokenExpiry, 'YYYY-MM-DD HH:mm:ss', true);
    if (!expiryMoment.isValid()) {
      // fallback parse
      expiryMoment = moment.utc(tokenExpiry);
      if (!expiryMoment.isValid()) return null;
    }
    const now = moment().utc();
    return expiryMoment.diff(now); // ms until expiry (can be negative)
  } catch (e) {
    console.error('Error calculating token expiry time:', e);
    return null;
  }
}

// ---------- the actual refresh call ----------
async function proactiveTokenRefresh() {
  // only one tab at a time
  if (!acquireRefreshLock(60000)) return;
  if (isRefreshing) { releaseRefreshLock(); return; }

  try {
    const currentToken = store && store.getters ? store.getters.userToken : null;
    if (!currentToken) { releaseRefreshLock(); return; }

    // adopt if another tab already refreshed meanwhile
    let latestToken = null, latestExpiry = null;
    try { latestToken = localStorage.getItem(TOKEN_KEY); } catch (_) { }
    try { latestExpiry = localStorage.getItem(EXPIRY_KEY); } catch (_) { }
    const vuexExpiry = store && store.getters ? store.getters.getTokenExpiry : null;
    if ((latestToken && latestToken !== currentToken) ||
      (latestExpiry && latestExpiry !== vuexExpiry)) {
      if (store && store.dispatch) {
        if (latestToken) store.dispatch('setUserToken', latestToken);
        if (latestExpiry) store.dispatch('setTokenExpiry', latestExpiry);
      }
      startRefreshTimer();
      releaseRefreshLock();
      return;
    }

    isRefreshing = true;

    // Call refresh endpoint (no need to pass Authorization if backend uses cookie / or keep it if needed)
    const refreshRes = await api.post('auth/refresh-token', {}, {
      headers: { Authorization: currentToken.indexOf('Bearer ') === 0 ? currentToken : ('Bearer ' + currentToken) }
    });

    const data = refreshRes && refreshRes.data ? refreshRes.data : {};
    const newToken = data.access_token || null;
    const newExpiry = data.expires_at || data.token_expiry || null;

    if (!newToken) throw new Error('No access_token in refresh response');
    if (store && store.dispatch) {
      store.dispatch('setUserToken', newToken);
      if (newExpiry) store.dispatch('setTokenExpiry', newExpiry);
    }

    // Publish to other tabs (triggers storage event there)
    publishToken(newToken, newExpiry);

    startRefreshTimer();

    onRefreshed(newToken);             // retry queued requests
  } catch (e) {
    // #1556: RECOVER, don't nuke. If this proactive refresh 401'd because another tab
    // already rotated the single-use token, the fresh one is in localStorage — adopt it,
    // drain the queue, and bail WITHOUT logging out. Only a genuine failure (no newer
    // token) falls through to the hard logout.
    let __lsTok = null; try { __lsTok = localStorage.getItem(TOKEN_KEY); } catch (_) { }
    const __stRaw = store && store.getters ? store.getters.userToken : null;
    const __stTok = (__stRaw && __stRaw.indexOf('Bearer ') === 0) ? __stRaw.slice(7) : __stRaw;
    if (__lsTok && __lsTok !== __stTok) {
      if (store && store.dispatch) {
        store.dispatch('setUserToken', __lsTok);
        let __e2 = null; try { __e2 = localStorage.getItem(EXPIRY_KEY); } catch (_) { }
        if (__e2) store.dispatch('setTokenExpiry', __e2);
      }
      try { onRefreshed(__lsTok); } catch (_) { }
      try { startRefreshTimer(); } catch (_) { }
      return;
    }
    // #1533/#1556: only nuke the shared session on an AUTHORITATIVE refresh-token 401
    // (no newer token, checked above). A non-401 (network/5xx) proactive-refresh failure
    // must NOT clear shared localStorage and log every tab out — bail and let the timer retry.
    if (!(e && e.response && e.response.status === 401)) {
      try { startRefreshTimer(); } catch (_) { }
      return;
    }
    // hard logout on refresh failure (authoritative)
    try {
      localStorage.removeItem('vuex');
      // #1767 — preserve unsaved-work drafts (see the helper at the top).
      clearLocalStorageExceptDrafts();
      document.cookie.split(';').forEach(function (c) {
        document.cookie = c.replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
      });
      if (store && store.dispatch) {
        store.dispatch('setUserToken', '');
        store.dispatch('setTokenExpiry', '');
        try { store.dispatch('setRefreshToken', ''); } catch (_) { }
      }
    } catch (_) { }
    if (window.location.hash !== '#/login') {
      window.location.href = '/#/login';
      setTimeout(() => {
        if (window.location.hash !== '#/login') window.location.reload();
      }, 100);
    }
  } finally {
    isRefreshing = false;
    releaseRefreshLock();
  }
}

// ---------- scheduling with 3-minute buffer ----------
function startRefreshTimer() {
  clearRefreshTimer();

  const msToExpiry = getTokenExpiryTime();
  if (msToExpiry == null || msToExpiry <= 0) return;

  // schedule = (time to expiry) - buffer - small jitter; never less than 60s
  const jitter = Math.floor(Math.random() * 3000);
  const refreshIn = Math.max(msToExpiry - BUFFER_MS - jitter, MIN_DELAY_MS);
  const refreshAt = Date.now() + refreshIn;

  // save the planned time so a tab that opens later can detect a missed schedule
  try { localStorage.setItem(REFRESH_AT_KEY, String(refreshAt)); } catch (_) { }

  refreshTimer = setTimeout(() => {
    // prefer the active tab to run it
    if (document.visibilityState === 'visible') {
      proactiveTokenRefresh();
    } else {
      // If hidden, we still attempt; lock stops duplicate storms across tabs
      proactiveTokenRefresh();
    }
  }, refreshIn);
}

// ---------- init entrypoint you call on login or app start ----------
function initializeProactiveRefresh() {
  const msToExpiry = getTokenExpiryTime();
  if (msToExpiry == null) return;

  let storedAt = 0;
  try { storedAt = parseInt(localStorage.getItem(REFRESH_AT_KEY) || '0', 10); } catch (_) { }

  // If a refresh time was missed (tabs were inactive/closed), refresh now.
  if (storedAt && Date.now() >= storedAt) {
    // If this tab is active, it will grab the lock and refresh.
    // If not active, it'll still attempt; only one tab will win the lock.
    proactiveTokenRefresh();
    return;
  }

  startRefreshTimer();
}

// ---------- refresh on resume if close to expiry ----------
window.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    const t = getTokenExpiryTime();
    console.log("Tab changed: Token expiry time: " + Math.floor(t / 60000) + " minutes");
    if (t != null && t < 3 * 60 * 1000) { // < 3 minutes left
      console.log("Tab changed: Token Refresh in progress");
      proactiveTokenRefresh();
    }
  }
});

// ---------- axios instance + interceptors ----------
const api = axios.create({
  baseURL: global[0].BASE_URL,
});

// Pre-flight before each request: if near expiry, refresh once first
let ensureInFlight = null;
async function ensureFreshToken(thresholdMs = 3 * 60 * 1000) {
  if (ensureInFlight) return ensureInFlight;
  ensureInFlight = (async () => {
    const msLeft = getTokenExpiryTime();
    if (msLeft == null || msLeft > thresholdMs) return;
    await proactiveTokenRefresh(); // uses cross-tab lock
  })();
  try { return await ensureInFlight; } finally { ensureInFlight = null; }
}

api.interceptors.request.use(
  async (config) => {
    config.headers = config.headers || {};
    try { await ensureFreshToken(3 * 60 * 1000); } catch (_) { }

    // ══ #1898 — ONE PLACE DECIDES WHAT Authorization GOES OUT ══
    //
    // This used to be `if (!config.headers.Authorization)`, which meant any
    // component supplying its own header bypassed normalisation entirely. Two
    // failures got through, and both look fine in the network tab:
    //
    //   "Bearer "      `userToken` is `"Bearer " + state.user.main.token`, so an
    //                  EMPTY token yields the literal string "Bearer " — truthy,
    //                  correctly prefixed, carrying nothing. The server runs
    //                  Str::after($h, 'Bearer '), gets '', and answers
    //                  401 Token not provided.
    //   "<bare token>" no prefix at all, so the same 401.
    //
    // That is why one page could 401 while 144 components doing the same thing
    // worked: they had a token in the store at the moment they asked.
    //
    // ⚠ The caller's token still WINS whenever it has one — some callers send a
    // different token deliberately (impersonation, a refresh probe) and
    // overriding that would break them silently. The store is a fallback for an
    // empty header, never an override.
    const token = store && store.getters ? store.getters.userToken : null;
    const resolvedAuth = resolveAuthHeader(config.headers.Authorization, token);
    if (resolvedAuth) {
      config.headers.Authorization = resolvedAuth;
    } else {
      // Nothing to send. Leaving a "Bearer " with no token makes the failure
      // look like a rejected token rather than a missing one.
      delete config.headers.Authorization;
    }

    // #1470 — global injection of cs3-context-role + cs3-context-clientId.
    //
    // History: #1440 added global injection; 2026-04-28 a source-level revert
    // dropped to a no-op here, but the runtime kept injecting because the
    // NAS gitlab-runner's webpack cache never picked up that revert. The
    // 2026-04-29 strategic decision was to keep the de-facto injected state.
    //
    // 2026-05-18 — making the injection explicit in source AND making it
    // AUTHORITATIVE: the interceptor overrides any caller-set value for
    // these two headers. That lets us fix the SA-sends-CA mismatch in one
    // place rather than chasing 100+ component-level header sites. Callers
    // that need to send a DIFFERENT role/client (e.g. switchClient.vue
    // validating a tenant switch) should call the API outside this `api`
    // instance, or set a request-level override flag that we honor here.
    //
    // canonicalRole is the user's TOP-LEVEL role (users.role_id) — sticky
    // across tenant switches. For SAs, this stays SA even while operating
    // inside a tenant. For everyone else, it equals their per-tenant role.
    //
    // Scope of override: ONLY cs3-context-role. cs3-context-clientId is left
    // alone because callers like switchClient.vue legitimately set it to a
    // tenant other than the user's current one (e.g. checking pending
    // privacy policies on the tenant they're switching TO).
    const canonicalRole = store && store.getters ? store.getters.userCanonicalRole : null;
    if (canonicalRole) {
      config.headers['cs3-context-role'] = canonicalRole;
    }
    // #1470 follow-up — restore cs3-context-clientId auto-injection that the
    // pre-MR-!85 runtime had. Components like useReportData.js rely on the
    // interceptor for header injection — without this, requests to
    // /api/v3/reports/* arrive with no clientId header and the backend's
    // FetchesContextData chain falls through to the JWT.client_id claim,
    // which for SAs logged into admin.callsteward.com is their "admin"
    // workspace Client (no employee data) → 200 with empty body.
    //
    // Fallback only — if the caller already set the header explicitly (e.g.
    // switchClient.vue's privacy-policy probe for a DIFFERENT tenant),
    // respect it.
    if (!config.headers['cs3-context-clientId']) {
      const ctxClientId = store && store.state && store.state.user && store.state.user.main
        ? store.state.user.main.clientId
        : null;
      if (ctxClientId) {
        config.headers['cs3-context-clientId'] = ctxClientId;
      }
    }

    // #1478 — never send the literal "undefined"/"null" (a JS undefined/null
    // coerced to a string by a caller, e.g. `'cs3-context-role': this.userRole`
    // when userRole is undefined). A MISSING context header makes the backend's
    // ValidateRoleHeader bypass cleanly; a literal "undefined" becomes a 403 on
    // legit traffic once #1432 flips flag -> enforce. Central backstop over the
    // 121 component header sites — strip any header whose value is one of these.
    Object.keys(config.headers).forEach((k) => {
      const v = config.headers[k];
      if (v === undefined || v === null || v === 'undefined' || v === 'null') {
        delete config.headers[k];
      }
    });

    return config;
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const response = error && error.response ? error.response : null;
    const originalConfig = error && error.config ? error.config : null;
    if (!response || response.status !== 401 || !originalConfig) return Promise.reject(error);

    const url = String(originalConfig.url || '');
    const isRefreshCall = url.indexOf('auth/refresh-token') !== -1;
    const isLoginCall = url.indexOf('auth/login') !== -1;
    const isLogoutCall = url.indexOf('logout') !== -1;
    const isWindowPage = window.location.hash && window.location.hash.indexOf('#/window/') === 0;
    if (isRefreshCall || isLoginCall || isLogoutCall || isWindowPage) {
      // No retry/refresh for auth calls, logout, or public window pages
      return Promise.reject(error);
    }

    // #1533/#1556 SAFETY NET: a 401 that already survived ONE successful refresh is an
    // AUTHORIZATION failure (e.g. a wrong/missing role header → ValidateRoleHeader), NOT
    // an expired token — refreshing again cannot clear it. Stop: surface the 401, never
    // loop a fresh-200-refresh into a storm → 429 → hard logout.
    if (originalConfig.__refreshRetried) {
      return Promise.reject(error);
    }

    // #1556: ADOPT-FIRST. A 401 here is usually a multi-tab rotation race — another
    // tab already rotated the (single-use) token, so this request went out carrying a
    // now-superseded one while the fresh token sits in localStorage/store. Before
    // triggering ANY refresh (which would POST the stale token → 401 → logout cascade),
    // adopt the newest token and retry ONCE with it. Only if there's genuinely no newer
    // token do we fall through to a (single-flight) refresh.
    const __bare = (t) => (t && t.indexOf('Bearer ') === 0) ? t.slice(7) : (t || '');
    const __reqToken = __bare((originalConfig.headers && originalConfig.headers.Authorization) || '');
    let __lsToken = null; try { __lsToken = localStorage.getItem(TOKEN_KEY); } catch (_) { }
    const __storeToken = __bare(store && store.getters ? store.getters.userToken : null);
    const __newest = (__lsToken && __lsToken !== __reqToken) ? __lsToken
                   : (__storeToken && __storeToken !== __reqToken) ? __storeToken
                   : null;
    if (!originalConfig.__authRetried && __newest) {
      if (__lsToken && __lsToken !== __storeToken && store && store.dispatch) {
        store.dispatch('setUserToken', __lsToken);
        let __lsExp = null; try { __lsExp = localStorage.getItem(EXPIRY_KEY); } catch (_) { }
        if (__lsExp) store.dispatch('setTokenExpiry', __lsExp);
      }
      const cfg = Object.assign({}, originalConfig, { headers: Object.assign({}, originalConfig.headers), __authRetried: true });
      cfg.headers.Authorization = 'Bearer ' + __newest;
      return api(cfg);
    }

    // if a refresh is in flight, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken) => {
          if (!newToken) return reject(new Error('No token received'));
          const cfg = Object.assign({}, originalConfig, { headers: Object.assign({}, originalConfig.headers) });
          cfg.headers.Authorization = newToken.indexOf('Bearer ') === 0 ? newToken : ('Bearer ' + newToken);
          resolve(api(cfg));
        });
      });
    }

    // try a reactive refresh (with lock) then retry once
    if (!acquireRefreshLock(15000)) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken) => {
          if (!newToken) return reject(new Error('No token received'));
          const cfg2 = Object.assign({}, originalConfig, { headers: Object.assign({}, originalConfig.headers) });
          cfg2.headers.Authorization = newToken.indexOf('Bearer ') === 0 ? newToken : ('Bearer ' + newToken);
          resolve(api(cfg2));
        });
      });
    }

    isRefreshing = true;
    try {
      const currentToken = store && store.getters ? store.getters.userToken : null;
      if (!currentToken) throw new Error('No current token available for refresh');

      const refreshRes = await api.post('auth/refresh-token', {}, {
        headers: { Authorization: currentToken.indexOf('Bearer ') === 0 ? currentToken : ('Bearer ' + currentToken) }
      });

      const data = refreshRes && refreshRes.data ? refreshRes.data : {};
      const newToken = data.access_token || null;
      const newExpiry = data.expires_at || data.token_expiry || null;
      if (!newToken) throw new Error('No access_token in refresh response');

      if (store && store.dispatch) {
        store.dispatch('setUserToken', newToken);
        if (newExpiry) store.dispatch('setTokenExpiry', newExpiry);
      }
      publishToken(newToken, newExpiry);
      startRefreshTimer();
      onRefreshed(newToken);

      // #1533/#1556: mark the post-refresh retry so that if it STILL 401s (authorization,
      // not token), the guard above rejects instead of refreshing again into a storm.
      const retryConfig = Object.assign({}, originalConfig, { headers: Object.assign({}, originalConfig.headers), __refreshRetried: true });
      retryConfig.headers.Authorization = newToken.indexOf('Bearer ') === 0 ? newToken : ('Bearer ' + newToken);
      return api(retryConfig);
    } catch (e) {
      // #1556: RECOVER, don't nuke. A refresh 401 during a multi-tab race means another
      // tab already rotated the single-use token — the fresh one is in localStorage.
      // Adopt it, drain the queue with it, and retry the original request ONCE instead
      // of hard-logging-out every same-origin tab. Only a genuine failure (no newer
      // token anywhere) falls through to the logout below.
      let __lsTok = null; try { __lsTok = localStorage.getItem(TOKEN_KEY); } catch (_) { }
      const __stRaw = store && store.getters ? store.getters.userToken : null;
      const __stTok = (__stRaw && __stRaw.indexOf('Bearer ') === 0) ? __stRaw.slice(7) : __stRaw;
      if (!originalConfig.__authRetried && __lsTok && __lsTok !== __stTok) {
        if (store && store.dispatch) {
          store.dispatch('setUserToken', __lsTok);
          let __e2 = null; try { __e2 = localStorage.getItem(EXPIRY_KEY); } catch (_) { }
          if (__e2) store.dispatch('setTokenExpiry', __e2);
        }
        try { onRefreshed(__lsTok); } catch (_) { }
        const cfgR = Object.assign({}, originalConfig, { headers: Object.assign({}, originalConfig.headers), __authRetried: true });
        cfgR.headers.Authorization = 'Bearer ' + __lsTok;
        return api(cfgR);
      }
      // propagate failure to queued requests
      for (let i = 0; i < refreshQueue.length; i++) {
        try { refreshQueue[i](null); } catch (_) { }
      }
      refreshQueue = [];
      // #1533/#1556: only NUKE the shared session when the failure is AUTHORITATIVE — the
      // refresh-token endpoint itself returned 401 (the refresh token is genuinely revoked)
      // AND there's no newer token (checked above). A non-401 refresh failure (network/5xx),
      // or a single stale-token tab, must NOT clear shared localStorage and log EVERY tab
      // out. Otherwise surface the error and leave the session intact.
      const __refreshWas401 = !!(e && e.response && e.response.status === 401);
      if (!__refreshWas401) {
        return Promise.reject(e);
      }
      // hard logout (authoritative session death only)
      try {
        localStorage.removeItem('vuex');
        // #1767 — preserve unsaved-work drafts across the hard logout. A 401 on
        // save triggers this path, and clearing wholesale destroyed the very
        // buffer that makes the work recoverable. Auth state is still fully
        // cleared; only `cs3-draft:` keys survive, and they carry no credentials.
        clearLocalStorageExceptDrafts();
        document.cookie.split(';').forEach(function (c) {
          document.cookie = c.replace(/^ +/, '')
            .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
        });
        if (store && store.dispatch) {
          store.dispatch('setUserToken', '');
          store.dispatch('setTokenExpiry', '');
          try { store.dispatch('setRefreshToken', ''); } catch (_) { }
        }
      } catch (_) { }
      // Don't redirect to login if on a public window page (token-auth, no JWT)
      if (window.location.hash && window.location.hash.indexOf('#/window/') === 0) {
        return Promise.reject(e);
      }
      if (window.location.hash !== '#/login') {
        window.location.href = '/#/login';
        setTimeout(() => { if (window.location.hash !== '#/login') window.location.reload(); }, 100);
      }
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
      releaseRefreshLock();
    }
  }
);

export { initializeProactiveRefresh, clearRefreshTimer, startRefreshTimer };
export default api;
